import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface LimitBadgeProps {
  /** Current usage count. */
  current: number;
  /**
   * Plan limit. `-1` is unlimited (rendered as `∞`),
   * `0` means the feature is disabled on the current plan.
   */
  limit: number;
  /** Singular/plural label rendered after the value, e.g. "products". */
  label: string;
  /** Optional icon (or any node). Defaults to no icon. */
  icon?: React.ReactNode;
  /** For matching the height of whatever toolbar it sits in. */
  className?: string;
  /** Optional override for the tooltip; falls back to a sensible default. */
  tooltip?: string;
  /** Smaller padding/font for dense headers. */
  compact?: boolean;
}

/**
 * Plan-limit usage chip.
 *
 * Same visual treatment as `CreditBadge` (bordered pill, medium value,
 * muted label suffix) but renders `current / limit`. Turns red at the cap so
 * vendors can see at a glance that they have maxed out.
 *
 * Why a shared component: every plan-gated surface (products, spaces,
 * leads, social accounts, scheduled posts, …) needs the same chip; one
 * source of truth keeps the visual language identical with credits.
 */
export const LimitBadge: React.FC<LimitBadgeProps> = ({
  current,
  limit,
  label,
  icon,
  className,
  tooltip,
  compact,
}) => {
  const { t } = useTranslation();

  const isUnlimited = limit < 0;
  const isDisabled = limit === 0;
  const isAtCap = !isUnlimited && current >= limit && !isDisabled;

  const limitDisplay = isUnlimited ? '∞' : limit.toLocaleString();
  const valueDisplay = `${current.toLocaleString()} / ${limitDisplay}`;

  const defaultTooltip = isUnlimited
    ? t('PLANS.LIMIT_BADGE.TOOLTIP_UNLIMITED', {
        defaultValue: '{{current}} {{label}} used (unlimited on this plan)',
        current,
        label,
      })
    : t('PLANS.LIMIT_BADGE.TOOLTIP', {
        defaultValue: '{{current}} of {{limit}} {{label}} used on this plan',
        current,
        limit,
        label,
      });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            // `rounded-lg` because it had no radius at all: a square-cornered
            // box beside rounded buttons reads as a different design system,
            // and this sits in a toolbar on five different pages.
            'inline-flex cursor-default items-center gap-1.5 rounded-lg border font-medium select-none',
            compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
            isAtCap ? 'border-destructive text-destructive' : 'border-input text-foreground',
            className,
          )}
        >
          {icon && <span className="flex items-center opacity-60">{icon}</span>}
          <span>{valueDisplay}</span>
          <span className="font-normal text-muted-foreground">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? defaultTooltip}</TooltipContent>
    </Tooltip>
  );
};

export default LimitBadge;
